import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { OUTCOME_LABELS } from "../CallOutcomeDialog";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

const TpsCard = ({ count, onDone }) => {
  const [text, setText] = useState("");
  const upload = async (replace) => {
    try {
      const { data } = await api.post("/calling/tps", { numbers: text, replace });
      toast.success(`${data.added} TPS numbers added (${data.total} total)`); setText(""); onDone();
    } catch (e) { err(e); }
  };
  const onFile = (f) => f && f.text().then(setText);
  return (
    <Card className="p-6 bg-card border-border/70 space-y-3">
      <h3 className="font-display font-semibold">UK TPS block list</h3>
      <p className="text-sm text-muted-foreground"><span data-testid="tps-count">{count}</span> numbers blocked. Upload the TPS/CTPS file you download from your TPS licence — any UK lead on it can't be dialed.</p>
      <input type="file" accept=".csv,.txt" onChange={(e) => onFile(e.target.files[0])} className="text-sm" data-testid="tps-file-input" />
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Or paste numbers, one per line" className="min-h-[90px]" data-testid="tps-textarea" />
      <div className="flex gap-2">
        <Button onClick={() => upload(false)} disabled={!text.trim()} data-testid="tps-add-button">Add to list</Button>
        <Button variant="outline" onClick={() => upload(true)} disabled={!text.trim()} data-testid="tps-replace-button">Replace list</Button>
      </div>
    </Card>
  );
};

export const SettingsTab = ({ subId, isAgency }) => {
  const [s, setS] = useState(null);
  const [pipelines, setPipelines] = useState([]);
  const load = () => api.get(`/calling/settings?sub_account_id=${subId}`).then(({ data }) => setS(data));
  useEffect(() => {
    load();
    api.get(`/pipelines?sub_account_id=${subId}`).then(({ data }) => setPipelines(data));
  }, [subId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
  const stages = pipelines.flatMap((p) => p.stages.map((st) => ({ key: `${p.id}:${st.id}`, label: `${p.name} → ${st.name}`, pipeline_id: p.id, stage_id: st.id })));
  const setMap = (o, key) => {
    const next = { ...s.outcome_stages };
    const st = stages.find((x) => x.key === key);
    if (st) next[o] = { pipeline_id: st.pipeline_id, stage_id: st.stage_id }; else delete next[o];
    setS({ ...s, outcome_stages: next });
  };
  const save = async () => {
    try { await api.put("/calling/settings", { sub_account_id: subId, record_calls: s.record_calls, outcome_stages: s.outcome_stages }); toast.success("Calling settings saved"); }
    catch (e) { err(e); }
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-card border-border/70 space-y-4">
        <label className="flex items-center justify-between gap-4">
          <span><span className="font-medium">Record calls</span><br /><span className="text-sm text-muted-foreground">Both sides hear "This call may be recorded" before talking.</span></span>
          <Switch disabled={!isAgency} checked={s.record_calls} onCheckedChange={(v) => setS({ ...s, record_calls: v })} data-testid="record-calls-switch" />
        </label>
        <div>
          <p className="font-medium mb-3">When a call outcome is logged, move the deal to…</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {s.outcomes.map((o) => {
              const m = s.outcome_stages[o];
              return (
                <label key={o} className="text-sm space-y-1">
                  <span className="text-muted-foreground">{OUTCOME_LABELS[o]}</span>
                  <select disabled={!isAgency} value={m ? `${m.pipeline_id}:${m.stage_id}` : ""} onChange={(e) => setMap(o, e.target.value)}
                    className="w-full h-9 rounded-md bg-secondary/50 border border-border/60 px-2" data-testid={`outcome-stage-${o}`}>
                    <option value="">Don't move</option>
                    {stages.map((st) => <option key={st.key} value={st.key}>{st.label}</option>)}
                  </select>
                </label>
              );
            })}
          </div>
        </div>
        {isAgency && <Button onClick={save} data-testid="save-calling-settings-button">Save settings</Button>}
      </Card>
      {isAgency && <TpsCard count={s.tps_count} onDone={load} />}
      <Card className="p-6 bg-card border-border/70 text-sm text-muted-foreground space-y-1.5">
        <p className="font-medium text-foreground">Always-on safeguards</p>
        <p>Every call is dialed by a person clicking Call — no auto-dialer, one call at a time.</p>
        <p>US leads can only be called 8am–9pm in their local time zone.</p>
        <p>Contacts marked Do not call, or UK numbers on your TPS list, can't be dialed.</p>
      </Card>
    </div>
  );
};
